import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020';

// Custom hook for API calls
export const useApiService = () => {
  const { getToken } = useAuth();

  // Generic API call method
  const apiCall = async (endpoint, options = {}) => {
    const token = getToken();
    
    const defaultOptions = {
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    };

    // Only set Content-Type for JSON, not for FormData
    if (!(options.body instanceof FormData)) {
      defaultOptions.headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        err.details = errorData;
        throw err;
      }

      // For DELETE operations, check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // No JSON content, return success object
        return { success: true, message: 'Operation completed successfully' };
      }
    } catch (error) {
      console.error('API call failed:', error);
      // Handle network errors specifically
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        const networkError = new Error('Network error: Unable to connect to the server. Please check if the backend is running.');
        networkError.isNetworkError = true;
        throw networkError;
      }
      throw error;
    }
  };

  // Auth helpers
  const requestPasswordReset = async (email) => {
    return apiCall('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  };

  const resetPassword = async ({ email, token, password }) => {
    return apiCall('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, password })
    });
  };

  // User API methods
  const createUser = async (userData = {}) => {
    return apiCall('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  };

  const getAllUsers = async () => {
    return apiCall('/api/users');
  };

  const getUserById = async (id) => {
    return apiCall(`/api/users/${id}`);
  };

  const getUserByEmail = async (email) => {
    return apiCall(`/api/users/email/${encodeURIComponent(email)}`);
  };

  const getCurrentUser = async () => {
    return apiCall('/api/users/me');
  };

  const createUserIfNotExists = async (userData) => {
    return apiCall('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  };

  const updateUser = async (id, userData) => {
    return apiCall(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  };

  const promoteToAdmin = async (id) => {
    return apiCall(`/api/users/${id}/promote-admin`, {
      method: 'PATCH',
    });
  };

  const deleteUser = async (id) => {
    return apiCall(`/api/users/${id}`, {
      method: 'DELETE',
    });
  };

  // Showroom API methods
  const createShowroom = async (showroomData = {}) => {
    return apiCall('/api/showrooms', {
      method: 'POST',
      body: JSON.stringify(showroomData),
    });
  };

  const getAllShowrooms = async () => {
    return apiCall('/api/showrooms');
  };

  const getShowroomById = async (id) => {
    return apiCall(`/api/showrooms/${id}`);
  };

  const updateShowroom = async (id, showroomData) => {
    return apiCall(`/api/showrooms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(showroomData),
    });
  };

  const updateShowroomStatus = async (id, status) => {
    return apiCall(`/api/showrooms/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  };

  const addUserToShowroom = async (userId, showroomId) => {
    return apiCall('/api/showrooms/add-user', {
      method: 'POST',
      body: JSON.stringify({ userId, showroomId }),
    });
  };



  const getUserShowroom = async () => {
    return apiCall('/api/showrooms/my/showroom');
  };

  const updateUserShowroom = async (showroomData) => {
    return apiCall('/api/showrooms/my/showroom', {
      method: 'PUT',
      body: JSON.stringify(showroomData),
    });
  };

  const getShowroomUsers = async () => {
    return apiCall('/api/showrooms/my/showroom/users');
  };

  // Get showrooms for dropdown (admin only)
  const getShowroomsForDropdown = async () => {
    return apiCall('/api/users/showrooms/dropdown');
  };

  // Get users by showroom (admin only)
  const getUsersByShowroom = async (showroomId) => {
    return apiCall(`/api/users/showroom/${showroomId}`);
  };

  // Credit API methods
  const getAllCredits = async () => {
    return apiCall('/api/credits');
  };

  const getCreditById = async (id) => {
    return apiCall(`/api/credits/${id}`);
  };

  const getCreditsByUserId = async (userId) => {
    return apiCall(`/api/credits/user/${userId}`);
  };

  const getUserBalance = async (userId) => {
    return apiCall(`/api/credits/balance/${userId}`);
  };

  const getUserCreditSummary = async (userId) => {
    return apiCall(`/api/credits/summary/${userId}`);
  };

  const createCredit = async (creditData) => {
    return apiCall('/api/credits', {
      method: 'POST',
      body: JSON.stringify(creditData),
    });
  };

  const updateCredit = async (id, creditData) => {
    return apiCall(`/api/credits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(creditData),
    });
  };

  const deleteCredit = async (id) => {
    return apiCall(`/api/credits/${id}`, {
      method: 'DELETE',
    });
  };

  // Customer API methods
  const createCustomer = async (customerData = {}) => {
    return apiCall('/api/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  };

  const getAllCustomers = async (params = {}) => {
    // params: { page, limit, search, clientType, status }
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/customers${query ? `?${query}` : ''}`);
  };

  const getCustomerById = async (id) => {
    return apiCall(`/api/customers/${id}`);
  };

  const getCustomerByCnic = async (cnic) => {
    return apiCall(`/api/customers/cnic/${cnic}`);
  };

  const getCustomerByPhone = async (phoneNumber) => {
    return apiCall(`/api/customers/phone/${phoneNumber}`);
  };

  const updateCustomer = async (id, customerData) => {
    return apiCall(`/api/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  };

  const deleteCustomer = async (id) => {
    return apiCall(`/api/customers/${id}`, {
      method: 'DELETE',
    });
  };

  const getCustomerStats = async () => {
    return apiCall('/api/customers/stats');
  };

  // Car API methods
  const createCar = async (carData = {}) => {
    return apiCall('/api/cars', {
      method: 'POST',
      body: JSON.stringify(carData),
    });
  };

  const getAllCars = async (params = {}) => {
    // params: { page, limit, search, condition, status }
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/cars${query ? `?${query}` : ''}`);
  };

  const getCarById = async (id) => {
    return apiCall(`/api/cars/${id}`);
  };

  const getCarByRegistration = async (registrationNumber) => {
    return apiCall(`/api/cars/registration/${registrationNumber}`);
  };

  const getCarByChassis = async (chassisNumber) => {
    return apiCall(`/api/cars/chassis/${chassisNumber}`);
  };

  const getCarByEngine = async (engineNumber) => {
    return apiCall(`/api/cars/engine/${engineNumber}`);
  };

  const updateCar = async (id, carData) => {
    return apiCall(`/api/cars/${id}`, {
      method: 'PUT',
      body: JSON.stringify(carData),
    });
  };

  const deleteCar = async (id) => {
    return apiCall(`/api/cars/${id}`, {
      method: 'DELETE',
    });
  };

  const getCarStats = async () => {
    return apiCall('/api/cars/stats');
  };

  // Deal API methods
  const createDeal = async (dealData) => {
    return apiCall('/api/deals', {
      method: 'POST',
      body: JSON.stringify(dealData),
    });
  };

  const getAllDeals = async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/api/deals?${queryString}`);
  };

  const getDealById = async (id) => {
    return apiCall(`/api/deals/${id}`);
  };

  const updateDeal = async (id, dealData) => {
    return apiCall(`/api/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dealData),
    });
  };

  const deleteDeal = async (id) => {
    return apiCall(`/api/deals/${id}`, {
      method: 'DELETE',
    });
  };

  const getDealStats = async () => {
    return apiCall('/api/deals/stats');
  };

  const getDealSummary = async (id) => {
    return apiCall(`/api/deals/${id}/summary`);
  };

  const addInstallment = async (dealId, installmentData) => {
    return apiCall(`/api/deals/${dealId}/installments`, {
      method: 'POST',
      body: JSON.stringify(installmentData),
    });
  };

  const markInstallmentPaid = async (dealId, installmentId) => {
    return apiCall(`/api/deals/${dealId}/installments/paid`, {
      method: 'PUT',
      body: JSON.stringify({ installmentId }),
    });
  };

  // Payment Structure API methods
  const getPaymentStructuresByDeal = async (dealId) => {
    return apiCall(`/api/payment-structures/deal/${dealId}`);
  };

  const createPaymentStructure = async (paymentData) => {
    return apiCall('/api/payment-structures', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  };

  const updatePaymentStatus = async (id, paymentStatus) => {
    return apiCall(`/api/payment-structures/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ paymentStatus }),
    });
  };

  const generateReceipt = async (id) => {
    return apiCall(`/api/payment-structures/${id}/receipt`, {
      method: 'POST',
    });
  };

  const deletePaymentStructure = async (id) => {
    return apiCall(`/api/payment-structures/${id}`, {
      method: 'DELETE',
    });
  };

  const getPaymentStatistics = async (dealId) => {
    return apiCall(`/api/payment-structures/deal/${dealId}/statistics`);
  };

  // Reporting API methods
  const getReportingData = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting${query ? `?${query}` : ''}`);
  };

  const getCustomerPaymentReport = async (customerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting/customer/${customerId}${query ? `?${query}` : ''}`);
  };

  const getProfitLossReport = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting/profit-loss${query ? `?${query}` : ''}`);
  };

  const getOutstandingPaymentsReport = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting/outstanding-payments${query ? `?${query}` : ''}`);
  };

  // Transaction report methods
  const getSellerTransactionReport = async (customerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting/seller/${customerId}${query ? `?${query}` : ''}`);
  };

  const getPurchaserTransactionReport = async (customerId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/reporting/purchaser/${customerId}${query ? `?${query}` : ''}`);
  };

  // Print DO methods
  const generatePurchaseDO = async (id) => {
    return apiCall(`/api/deals/${id}/print/purchase-do`);
  };

  const generateSellDO = async (id) => {
    return apiCall(`/api/deals/${id}/print/sell-do`);
  };

  // Deal Attachment API methods
  const uploadDealAttachment = async (dealId, file, description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    return apiCall(`/api/deals/${dealId}/attachments`, {
      method: 'POST',
      body: formData,
    });
  };

  const getDealAttachments = async (dealId) => {
    return apiCall(`/api/deals/${dealId}/attachments`);
  };

  const deleteDealAttachment = async (dealId, attachmentId) => {
    return apiCall(`/api/deals/${dealId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  };

  const downloadDealAttachment = async (dealId, attachmentId) => {
    return apiCall(`/api/deals/${dealId}/attachments/${attachmentId}/download`);
  };

  // Daily Ledger API methods
  const getAllDailyLedger = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/daily-ledger${query ? `?${query}` : ''}`);
  };

  const getDailyLedgerById = async (id) => {
    return apiCall(`/api/daily-ledger/${id}`);
  };

  const createDailyLedger = async (dailyLedgerData) => {
    return apiCall('/api/daily-ledger', {
      method: 'POST',
      body: JSON.stringify(dailyLedgerData),
    });
  };

  const updateDailyLedger = async (id, dailyLedgerData) => {
    return apiCall(`/api/daily-ledger/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dailyLedgerData),
    });
  };

  const deleteDailyLedger = async (id) => {
    return apiCall(`/api/daily-ledger/${id}`, {
      method: 'DELETE',
    });
  };

  const getDailyLedgerStats = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/daily-ledger/stats${query ? `?${query}` : ''}`);
  };

  // Dashboard API methods
  const getDashboardStats = async () => {
    return apiCall('/api/dashboard/stats');
  };

  return {
    createUser,
    getAllUsers,
    getUserById,
    getUserByEmail,
    getCurrentUser,
    createUserIfNotExists,
    updateUser,
    promoteToAdmin,
    deleteUser,
    createShowroom,
    getAllShowrooms,
    getShowroomById,
    updateShowroom,
    updateShowroomStatus,
    addUserToShowroom,

    getUserShowroom,
    updateUserShowroom,
    getShowroomUsers,
    getShowroomsForDropdown,
    getUsersByShowroom,
    getAllCredits,
    getCreditById,
    getCreditsByUserId,
    getUserBalance,
    getUserCreditSummary,
    createCredit,
    updateCredit,
    deleteCredit,
    createCustomer,
    getAllCustomers,
    getCustomerById,
    getCustomerByCnic,
    getCustomerByPhone,
    updateCustomer,
    deleteCustomer,
    getCustomerStats,
    createCar,
    getAllCars,
    getCarById,
    getCarByRegistration,
    getCarByChassis,
    getCarByEngine,
    updateCar,
    deleteCar,
    getCarStats,
    createDeal,
    getAllDeals,
    getDealById,
    updateDeal,
    deleteDeal,
    getDealStats,
    getDealSummary,
    addInstallment,
    markInstallmentPaid,
    getPaymentStructuresByDeal,
    createPaymentStructure,
    updatePaymentStatus,
    generateReceipt,
    deletePaymentStructure,
    getPaymentStatistics,
    getReportingData,
    getCustomerPaymentReport,
    getProfitLossReport,
    getOutstandingPaymentsReport,
    getSellerTransactionReport,
    getPurchaserTransactionReport,
    generatePurchaseDO,
    generateSellDO,
    uploadDealAttachment,
    getDealAttachments,
    deleteDealAttachment,
    downloadDealAttachment,
    getAllDailyLedger,
    getDailyLedgerById,
    createDailyLedger,
    updateDailyLedger,
    deleteDailyLedger,
    getDailyLedgerStats,
    getDashboardStats,
    requestPasswordReset,
    resetPassword,
  };
}; 